import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Wifi, Utensils, Shield, Zap, Home as HomeIcon, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const features = [
    { icon: Wifi, title: content.feature_wifi_title || 'High-Speed WiFi', description: content.feature_wifi_desc || 'Unlimited internet connectivity' },
    { icon: Utensils, title: content.feature_food_title || 'Food Included', description: content.feature_food_desc || 'Nutritious meals 3 times a day' },
    { icon: Shield, title: content.feature_security_title || '24/7 Security', description: content.feature_security_desc || 'CCTV surveillance & guards' },
    { icon: Zap, title: content.feature_power_title || 'Power Backup', description: content.feature_power_desc || 'Uninterrupted electricity' },
    { icon: HomeIcon, title: content.feature_furnished_title || 'Fully Furnished', description: content.feature_furnished_desc || 'Move-in ready spaces' },
    { icon: Users, title: content.feature_community_title || 'Community', description: content.feature_community_desc || 'Like-minded residents' },
  ];

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
                {content.home_hero_title || 'Your Perfect'}
                <span className="text-brand-purple"> Stay</span> Awaits
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-gray-600 mb-8 font-body">
                {content.home_hero_subtitle || 'Premium Paying Guest & Home Stay accommodations designed for students and working professionals. Experience comfort, convenience, and community at its finest.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/properties"
                  data-testid="home-explore-button"
                  className="bg-brand-purple text-white px-8 py-4 rounded-full font-body text-lg font-medium hover:bg-opacity-90 transition-all shadow-lg shadow-purple-900/20 hover:shadow-xl hover:shadow-purple-900/30 text-center"
                >
                  Explore Properties
                </Link>
                <Link
                  to="/inquiry"
                  data-testid="home-inquiry-button"
                  className="bg-white text-brand-purple border-2 border-brand-purple px-8 py-4 rounded-full font-body text-lg font-medium hover:bg-brand-purple hover:text-white transition-all text-center"
                >
                  Inquire Now
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pgstay-1/artifacts/eti3463v_final.png"
                alt="Modern comfortable home stay bedroom"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-square"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
              {content.home_why_choose_title || 'Why Choose INDWELLER STAY?'}
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-2xl mx-auto">
              {content.home_why_choose_subtitle || 'We provide everything you need for a comfortable living experience'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-2xl hover:shadow-[0_8px_16px_rgba(91,45,139,0.08)] transition-all duration-300"
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 bg-brand-purple/10 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="text-brand-purple" size={28} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 font-body">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gradient-to-br from-brand-purple to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-6">
            {content.home_cta_title || 'Ready to Find Your New Home?'}
          </h2>
          <p className="text-lg md:text-xl leading-relaxed opacity-90 mb-8 font-body max-w-2xl mx-auto">
            {content.home_cta_subtitle || 'Join hundreds of happy residents living their best life at INDWELLER STAY'}
          </p>
          <Link
            to="/inquiry"
            data-testid="home-cta-button"
            className="inline-block bg-brand-orange text-white px-12 py-4 rounded-full font-body text-lg font-medium hover:bg-opacity-90 transition-all shadow-lg"
          >
            Get Started Today
          </Link>
        </div>
      </section>
    </div>
  );
};
